import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { MarketerEarning, MarketerEarningSummary } from "./types/earningTypes";
import {
  getMarketerEarningsService,
  getMarketerEarningSummaryService,
} from "../../apis/earnings";

interface EarningsState {
  marketerEarnings: MarketerEarning[];
  marketerEarningSummary: MarketerEarningSummary | null;
  loading: boolean;
  error: string | null;
}

const initialState: EarningsState = {
  marketerEarnings: [],
  marketerEarningSummary: null,
  loading: false,
  error: null,
};

// Async thunk to fetch marketer earnings
export const fetchMarketerEarnings = createAsyncThunk(
  "earnings/fetchMarketerEarnings",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getMarketerEarningsService();
      return data?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Failed to fetch earnings");
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch earnings"
      );
    }
  }
);

// Async thunk to fetch marketer earning summary
export const fetchMarketerEarningSummary = createAsyncThunk(
  "earnings/fetchMarketerEarningSummary",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getMarketerEarningSummaryService();
      return data?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch earning summary"
      );
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch earning summary"
      );
    }
  }
);

const earningsSlice = createSlice({
  name: "earnings",
  initialState,
  reducers: {
    resetEarningsState: (state) => {
      state.marketerEarnings = [];
      state.marketerEarningSummary = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch marketer earnings
    builder
      .addCase(fetchMarketerEarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMarketerEarnings.fulfilled,
        (state, action: PayloadAction<MarketerEarning[]>) => {
          state.loading = false;
          state.marketerEarnings = action.payload;
        }
      )
      .addCase(
        fetchMarketerEarnings.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );

    // Fetch marketer earning summary
    builder
      .addCase(fetchMarketerEarningSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMarketerEarningSummary.fulfilled,
        (state, action: PayloadAction<MarketerEarningSummary>) => {
          state.loading = false;
          state.marketerEarningSummary = action.payload;
        }
      )
      .addCase(
        fetchMarketerEarningSummary.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { resetEarningsState } = earningsSlice.actions;
export default earningsSlice.reducer;
